import { useCallback, useEffect, useState } from 'react';
import {
  fetchPOs, fetchCommitment, createPO, decidePO, progressPO, myMemberId,
  PO_CATEGORIES, type PO,
} from '../lib/purchasing';
import { TENANT } from '@tenant';
import { onFail } from '../lib/errors';
import './purchasing.css';

/* =====================================================================================
   Purchasing: the spend is agreed before the invoice exists.

   Small amounts auto-approve within the policy thresholds the organisation already set for
   expenses; bigger ones route to the line manager; director-level and capital items go to
   the owner. Approved orders are the open commitment finance watches, and received orders
   are what the invoice reconciles against.
   ===================================================================================== */

const money = (n: number, c: string) => new Intl.NumberFormat('en-GB',
  { style: 'currency', currency: c, maximumFractionDigits: 2 }).format(n);
const STATUS_PILL: Record<string, string> = {
  requested: 'info', approved: 'ok', refused: 'warn', ordered: 'accent',
  received: 'mute', cancelled: 'mute',
};

export function PurchasingView() {
  const [pos, setPOs] = useState<PO[]>([]);
  const [commit, setCommit] = useState<Awaited<ReturnType<typeof fetchCommitment>>>([]);
  const [me, setMe] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchPOs().then(setPOs).catch(onFail('Purchase orders', () => setPOs([])));
    fetchCommitment().then(setCommit).catch(() => setCommit([]));
    myMemberId().then(setMe).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const forMe = pos.filter((p) => p.status === 'requested' && p.approver_id === me && p.requested_by !== me);

  return (
    <div className="page purchasing">
      <div className="page-head">
        <h1 className="page-title">Purchasing</h1>
        <p className="page-sub">
          Commit spend before the invoice, not after. Small amounts approve themselves within
          policy; larger ones route exactly as expenses do.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {forMe.length > 0 && (
        <section className="card po-approvals">
          <h2>Awaiting your decision</h2>
          {forMe.map((p) => (
            <div key={p.id} className="po-approval">
              <div>
                <strong>{p.reference}</strong> · {p.supplier_name} ·{' '}
                {money(Number(p.amount), p.currency)}
                {p.is_capital && <span className="pill warn">capital</span>}
                <div className="cell-sub">{p.description}</div>
                {p.justification && <div className="cell-sub">{p.justification}</div>}
              </div>
              <div className="po-actions">
                <button className="btn primary" onClick={() =>
                  decidePO(p.id, true).then(() => { setMsg(`${p.reference} approved.`); load(); })
                    .catch(onFail('The decision'))}>Approve</button>
                <button className="btn" onClick={() => {
                  const note = window.prompt('Say why:');
                  if (note === null) return;
                  decidePO(p.id, false, note).then(() => { setMsg(`${p.reference} refused.`); load(); })
                    .catch(onFail('The decision'));
                }}>Refuse</button>
              </div>
            </div>
          ))}
        </section>
      )}

      <div className="metrics">
        {commit.filter((c) => c.committed || c.awaiting_decision).map((c) => (
          <div key={c.currency} className="metric card">
            <div className="label">Open commitment, {c.currency}</div>
            <div className="metric-value">{money(Number(c.committed ?? 0), c.currency)}</div>
            <div className="metric-foot"><span>
              approved or ordered, not yet received
              {c.awaiting_decision ? ` · ${c.awaiting_decision} awaiting decision` : ''}
              {c.overdue_delivery ? ` · ${c.overdue_delivery} overdue delivery` : ''}
            </span></div>
          </div>
        ))}
      </div>

      <div className="toolbar">
        <button className="btn primary" onClick={() => setCreating(true)}>Raise a purchase order</button>
      </div>

      <section className="card">
        {pos.length === 0 && (
          <p className="module-empty">
            Nothing yet. The first invoice that arrives for something nobody agreed to will
            explain why this page exists.
          </p>
        )}
        {pos.length > 0 && (
          <table className="data">
            <thead><tr><th>Ref</th><th>Supplier</th><th>What</th><th className="num">Amount</th>
              <th>Status</th><th /></tr></thead>
            <tbody>
              {pos.map((p) => (
                <tr key={p.id}>
                  <td className="mono">{p.reference}</td>
                  <td>{p.supplier_name}</td>
                  <td>
                    <div className="cell-title">{p.description}</div>
                    <div className="cell-sub">{PO_CATEGORIES.find(([k]) => k === p.category)?.[1] ?? p.category}
                      {p.needed_by && <> · needed by {p.needed_by}</>}</div>
                  </td>
                  <td className="num">{money(Number(p.amount), p.currency)}</td>
                  <td><span className={`pill ${STATUS_PILL[p.status] ?? 'mute'}`}>
                    {p.auto_approved && p.status === 'approved' ? 'approved (within policy)' : p.status}</span>
                    {p.decision_note && <div className="cell-sub">{p.decision_note}</div>}
                  </td>
                  <td className="num po-progress">
                    {p.status === 'approved' && (
                      <button className="link-btn" onClick={() =>
                        progressPO(p.id, 'ordered').then(load).catch(onFail('The order'))}>Mark ordered</button>
                    )}
                    {p.status === 'ordered' && (
                      <button className="link-btn" onClick={() =>
                        progressPO(p.id, 'received').then(load).catch(onFail('The order'))}>Mark received</button>
                    )}
                    {(p.status === 'requested' || p.status === 'approved') && (
                      <button className="link-btn" onClick={() => {
                        const note = window.prompt('Cancel this order? Say why:');
                        if (note === null) return;
                        progressPO(p.id, 'cancelled', note).then(load).catch(onFail('The order'));
                      }}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {creating && (
        <POForm onClose={() => setCreating(false)}
                onDone={(auto) => { setCreating(false);
                  setMsg(auto ? 'Raised and approved within policy: order it when ready.'
                              : 'Raised. Your approver has been told.'); load(); }} />
      )}
    </div>
  );
}

function POForm({ onClose, onDone }: { onClose: () => void; onDone: (auto: boolean) => void }) {
  const BASE = TENANT.brand.baseCurrency ?? 'GBP';
  const [f, setF] = useState({ supplier: '', description: '', amount: '', currency: BASE,
    category: 'goods', neededBy: '', justification: '', quoteRef: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer po-drawer">
        <h2>Raise a purchase order</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Supplier</span>
          <input className="input" value={f.supplier} onChange={set('supplier')} /></label>
        <label className="cf-field"><span>What is being bought</span>
          <input className="input" value={f.description} onChange={set('description')} /></label>
        <div className="po-two">
          <label className="cf-field"><span>Amount</span>
            <input className="input" type="number" min="0.01" step="0.01" value={f.amount}
                   onChange={set('amount')} /></label>
          <label className="cf-field"><span>Currency</span>
            <select className="input sel" value={f.currency} onChange={set('currency')}>
              {[BASE, ...['GBP', 'EUR', 'USD', 'CHF', 'SEK'].filter((c) => c !== BASE)]
                .map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
        </div>
        <div className="po-two">
          <label className="cf-field"><span>Category</span>
            <select className="input sel" value={f.category} onChange={set('category')}>
              {PO_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          <label className="cf-field"><span>Needed by</span>
            <input className="input" type="date" value={f.neededBy} onChange={set('neededBy')} /></label>
        </div>
        <label className="cf-field"><span>Why, in one line your approver will read</span>
          <input className="input" value={f.justification} onChange={set('justification')} /></label>
        <label className="cf-field"><span>Quote reference (optional)</span>
          <input className="input" value={f.quoteRef} onChange={set('quoteRef')} /></label>
        <div className="exp-decide-actions">
          <button className="btn primary"
                  disabled={busy || !f.supplier.trim() || !f.description.trim() || !(Number(f.amount) > 0)}
                  onClick={async () => {
                    setBusy(true); setErr(null);
                    try {
                      await createPO({ supplier: f.supplier, description: f.description,
                        amount: Number(f.amount), currency: f.currency, category: f.category,
                        neededBy: f.neededBy || undefined, justification: f.justification || undefined,
                        quoteRef: f.quoteRef || undefined });
                      onDone(false);
                    } catch (e) { setErr(String((e as Error).message)); setBusy(false); }
                  }}>Raise it</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}
